import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import { PROFILE_ORDER } from "lib/consts/robotProfiles";
import {
    getRobotProfile,
    getRobotProfileBaseUrl,
    type RobotProfileId,
} from "lib/robotProfileApi";
import { TOPIC_STATE_CONNECTED, parseConnectedPayload } from "lib/robotLinkFeedback";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";

/** Link-loss grace after `/state/connected` goes false. */
const LINK_LOSS_GRACE_MS = 10_000;
const PROFILE_POLL_MS = 5_000;

export { TOPIC_STATE_CONNECTED } from "lib/robotLinkFeedback";

type RobotProfileLivenessContextValue = {
    robotLinkConnected: boolean | null;
    profileRunning: boolean;
    /** Active profile id while `profileRunning`; otherwise null. */
    activeProfile: RobotProfileId | null;
    /** Re-fetch running/active from the robot-profile API. */
    refreshProfileStatus: () => Promise<void>;
    /** Apply local snapshot immediately after settings connect/disconnect. */
    setProfileSnapshot: (snapshot: RobotProfileSnapshot) => void;
};

type RobotProfileSnapshot = {
    running: boolean;
    activeProfile: RobotProfileId | null;
};

const RobotProfileLivenessContext = createContext<RobotProfileLivenessContextValue>({
    robotLinkConnected: null,
    profileRunning: false,
    activeProfile: null,
    refreshProfileStatus: async () => {},
    setProfileSnapshot: () => {},
});
RobotProfileLivenessContext.displayName = "RobotProfileLiveness";

export const useRobotProfileLiveness = () => useContext(RobotProfileLivenessContext);

/**
 * Watches `/state/connected` while a robot profile is running.
 * After true→false for LINK_LOSS_GRACE_MS, warns without changing the profile.
 */
const RobotProfileLivenessProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation("settings");
    const { selectedARC, controlSocket, connectionState } = useARC();
    const { addToast } = useToast();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();

    const baseUrl = useMemo(
        () => getRobotProfileBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [profileRunning, setProfileRunning] = useState(false);
    const [activeProfile, setActiveProfile] = useState<RobotProfileId | null>(null);
    const [robotLinkConnected, setRobotLinkConnected] = useState<boolean | null>(null);

    const sawConnectedTrueRef = useRef(false);
    const graceTimerRef = useRef<number | null>(null);
    const linkLossWarnedRef = useRef(false);

    const clearGrace = useCallback(() => {
        if (graceTimerRef.current != null) {
            window.clearTimeout(graceTimerRef.current);
            graceTimerRef.current = null;
        }
    }, []);

    const refreshRunning = useCallback(async () => {
        if (!baseUrl) {
            setProfileRunning(false);
            setActiveProfile(null);
            return;
        }
        try {
            const res = await getRobotProfile(baseUrl);
            const running = Boolean(res.profile?.running);
            const activeRaw = res.profile?.active;
            const active =
                running &&
                typeof activeRaw === "string" &&
                PROFILE_ORDER.includes(activeRaw as RobotProfileId)
                    ? (activeRaw as RobotProfileId)
                    : null;
            setProfileRunning(running);
            setActiveProfile(active);
        } catch {
            // ignore
        }
    }, [baseUrl]);

    const setProfileSnapshot = useCallback((snapshot: RobotProfileSnapshot) => {
        setProfileRunning(snapshot.running);
        setActiveProfile(snapshot.running ? snapshot.activeProfile : null);
    }, []);

    useEffect(() => {
        void refreshRunning();
        if (!baseUrl) return;
        const id = window.setInterval(() => void refreshRunning(), PROFILE_POLL_MS);
        return () => window.clearInterval(id);
    }, [baseUrl, refreshRunning]);

    const warnLinkLost = useCallback(() => {
        if (linkLossWarnedRef.current) return;
        linkLossWarnedRef.current = true;
        addToast({
            type: "warning",
            duration: 8000,
            message: t("robot.linkLost", {
                defaultValue: "Robot link lost. Check the robot connection.",
            }),
        });
    }, [addToast, t]);

    const onConnectedMessage = useCallback(
        (data: unknown) => {
            const linked = parseConnectedPayload(data);
            if (linked === null) return;
            setRobotLinkConnected(linked);

            if (!profileRunning) {
                clearGrace();
                linkLossWarnedRef.current = false;
                return;
            }

            if (linked) {
                sawConnectedTrueRef.current = true;
                clearGrace();
                linkLossWarnedRef.current = false;
                return;
            }

            // Only warn after a previously healthy link has remained lost.
            if (!sawConnectedTrueRef.current) return;
            if (linkLossWarnedRef.current) return;
            if (graceTimerRef.current != null) return;
            graceTimerRef.current = window.setTimeout(() => {
                graceTimerRef.current = null;
                warnLinkLost();
            }, LINK_LOSS_GRACE_MS);
        },
        [clearGrace, profileRunning, warnLinkLost]
    );

    useEffect(() => {
        if (
            !profileRunning ||
            !controlSocket ||
            connectionState !== ConnectionState.Connected
        ) {
            clearGrace();
            sawConnectedTrueRef.current = false;
            linkLossWarnedRef.current = false;
            setRobotLinkConnected(null);
            return;
        }

        const detach = attachRosTopicWithRetry(
            controlSocket,
            TOPIC_STATE_CONNECTED,
            onConnectedMessage,
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                messageTypeFallback: "std_msgs/msg/Bool",
                subscribeQos: {
                    durability: "transient_local",
                    reliability: "reliable",
                    history: "keep_last",
                    depth: 1,
                },
                markMessageReceived: (data) => parseConnectedPayload(data) !== null,
            }
        );

        return () => {
            detach();
            clearGrace();
        };
    }, [clearGrace, connectionState, controlSocket, onConnectedMessage, profileRunning]);

    const value = useMemo(
        () => ({
            robotLinkConnected,
            profileRunning,
            activeProfile,
            refreshProfileStatus: refreshRunning,
            setProfileSnapshot,
        }),
        [activeProfile, profileRunning, refreshRunning, robotLinkConnected, setProfileSnapshot]
    );

    return (
        <RobotProfileLivenessContext.Provider value={value}>
            {children}
        </RobotProfileLivenessContext.Provider>
    );
};

export default RobotProfileLivenessProvider;
